"use client";

import {
  FC,
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
} from "react";
import { usePathname, useSearchParams } from "./navigation";

const NavigationProxyContext = createContext<NavigationProxyInstance>([
  undefined,
  () => {
    /* noop */
  },
]);

/**
 * Holds an optional "unsaved changes" guard message. When set, in-app navigations
 * (via the app <Link> and useRouter) prompt `window.confirm(tips)` before leaving,
 * and a `beforeunload` handler guards full-page reloads / tab close.
 *
 * (The previous Next implementation also intercepted browser back/forward via an
 * injected inline script; in the SPA the in-app Link/useRouter confirm covers the
 * common cases and the beforeunload handler covers hard reloads.)
 */
const NavigationProxyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [tips, setTips] = useState<string | undefined>();
  const msg =
    tips === undefined ? tips : tips || "Are you sure want to leave this page?";

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const url = [pathname, searchParams.toString()].filter(Boolean).join("?");
  const [prevUrl, setPrevUrl] = useState(url);
  if (url !== prevUrl) {
    setPrevUrl(url);
    setTips(undefined);
  }

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (msg === undefined) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [msg]);

  return (
    <NavigationProxyContext.Provider value={[msg, setTips]}>
      {children}
    </NavigationProxyContext.Provider>
  );
};

export type NavigationProxyInstance = [
  string | undefined,
  (tips?: string) => void,
];

export { NavigationProxyContext, NavigationProxyProvider };
